import { useState } from 'react'
import { Link, useNavigate } from '@tanstack/react-router'
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { z } from 'zod'
import { ArrowLeft, Info, X } from 'lucide-react'

import { Button } from '@/components/ui/button'
import { Card, CardBody, CardHeader } from '@/components/ui/card'
import { Input, Textarea } from '@/components/ui/field'
import { useToast } from '@/components/ui/toast'
import { useCreateJob } from '@/features/marketplace/api'
import { ApiError } from '@/lib/api'
import { formatRupiah } from '@/lib/format'

const schema = z
  .object({
    title: z.string().min(8, 'Judul minimal 8 karakter.').max(140),
    description: z.string().min(30, 'Uraian minimal 30 karakter agar jelas bagi tenaga ahli.').max(8000),
    category: z.string().min(2, 'Kategori wajib diisi.').max(60),
    budget_min: z.coerce.number().min(0),
    budget_max: z.coerce.number().min(1, 'Anggaran maksimum wajib diisi.'),
    duration_days: z.coerce.number().min(0).max(730).optional(),
  })
  .refine((values) => values.budget_max >= values.budget_min, {
    message: 'Anggaran maksimum tidak boleh lebih kecil dari minimum.',
    path: ['budget_max'],
  })

type FormValues = z.input<typeof schema>

export function NewProjectPage() {
  const [skills, setSkills] = useState<string[]>([])
  const [skillDraft, setSkillDraft] = useState('')
  const createJob = useCreateJob()
  const navigate = useNavigate()
  const toast = useToast()

  const form = useForm<FormValues>({
    resolver: zodResolver(schema),
    defaultValues: {
      title: '',
      description: '',
      category: '',
      budget_min: 0,
      budget_max: 0,
      duration_days: 14,
    },
  })

  const budgetMax = Number(form.watch('budget_max') ?? 0)
  const estimatedFee = Math.floor((budgetMax * 10) / 100)

  const addSkill = () => {
    const value = skillDraft.trim().toLowerCase()
    if (!value || skills.includes(value) || skills.length >= 12) return
    setSkills((current) => [...current, value])
    setSkillDraft('')
  }

  const submit = form.handleSubmit((values) => {
    createJob.mutate(
      {
        title: values.title,
        description: values.description,
        category: values.category.toLowerCase().trim(),
        skills,
        budget_min: Number(values.budget_min),
        budget_max: Number(values.budget_max),
        duration_days: values.duration_days ? Number(values.duration_days) : undefined,
      },
      {
        onSuccess: (data) => {
          toast.success('Proyek dipublikasikan', 'Tenaga ahli sudah dapat mengirim proposal.')
          navigate({ to: '/proyek/$jobId', params: { jobId: data.job_post.id } })
        },
        onError: (error) =>
          toast.error('Gagal memposting proyek', error instanceof ApiError ? error.message : undefined),
      },
    )
  })

  return (
    <div className="space-y-6">
      <Link
        to="/app/proyek"
        className="inline-flex items-center gap-1.5 text-sm font-semibold text-muted transition hover:text-ink"
      >
        <ArrowLeft className="size-4" />
        Proyek saya
      </Link>

      <header>
        <h1 className="text-2xl font-extrabold tracking-tight text-ink sm:text-3xl">Posting proyek baru</h1>
        <p className="mt-1 text-muted">
          Makin jelas kebutuhannya, makin tepat proposal yang Anda terima.
        </p>
      </header>

      <div className="grid gap-6 lg:grid-cols-[1.6fr_1fr]">
        <Card>
          <CardHeader title="Detail kebutuhan" />
          <CardBody>
            <form onSubmit={submit} className="space-y-4" noValidate>
              <Input
                label="Judul proyek"
                required
                placeholder="Misalnya: Dasbor analitik penjualan untuk jaringan toko"
                error={form.formState.errors.title?.message}
                {...form.register('title')}
              />

              <Textarea
                label="Uraian pekerjaan"
                required
                rows={8}
                placeholder="Apa yang ingin dicapai, data atau akses apa yang tersedia, dan seperti apa hasil akhir yang diharapkan."
                error={form.formState.errors.description?.message}
                {...form.register('description')}
              />

              <div className="grid gap-4 sm:grid-cols-2">
                <Input
                  label="Kategori"
                  required
                  placeholder="data, desain, pengembangan…"
                  error={form.formState.errors.category?.message}
                  {...form.register('category')}
                />
                <Input
                  label="Perkiraan durasi (hari)"
                  type="number"
                  min={0}
                  error={form.formState.errors.duration_days?.message}
                  {...form.register('duration_days')}
                />
              </div>

              <div className="grid gap-4 sm:grid-cols-2">
                <Input
                  label="Anggaran minimum"
                  type="number"
                  min={0}
                  step={500000}
                  prefix="Rp"
                  error={form.formState.errors.budget_min?.message}
                  {...form.register('budget_min')}
                />
                <Input
                  label="Anggaran maksimum"
                  type="number"
                  min={0}
                  step={500000}
                  prefix="Rp"
                  required
                  error={form.formState.errors.budget_max?.message}
                  {...form.register('budget_max')}
                />
              </div>

              <div>
                <label htmlFor="skill-input" className="text-sm font-semibold text-ink">
                  Keahlian yang dibutuhkan
                </label>
                <div className="mt-1.5 flex gap-2">
                  <Input
                    id="skill-input"
                    value={skillDraft}
                    onChange={(event) => setSkillDraft(event.target.value)}
                    onKeyDown={(event) => {
                      if (event.key === 'Enter') {
                        event.preventDefault()
                        addSkill()
                      }
                    }}
                    placeholder="sql, figma, react…"
                  />
                  <Button type="button" variant="outline" onClick={addSkill}>
                    Tambah
                  </Button>
                </div>

                {skills.length > 0 ? (
                  <ul className="mt-3 flex flex-wrap gap-2">
                    {skills.map((skill) => (
                      <li key={skill}>
                        <button
                          type="button"
                          onClick={() => setSkills((current) => current.filter((item) => item !== skill))}
                          className="inline-flex items-center gap-1.5 rounded-full bg-brand-soft px-3 py-1 text-sm font-medium text-brand-ink"
                        >
                          {skill}
                          <X className="size-3.5" />
                        </button>
                      </li>
                    ))}
                  </ul>
                ) : null}
              </div>

              <div className="flex justify-end gap-2 pt-2">
                <Link to="/app/proyek">
                  <Button type="button" variant="ghost">
                    Batal
                  </Button>
                </Link>
                <Button type="submit" loading={createJob.isPending}>
                  Publikasikan proyek
                </Button>
              </div>
            </form>
          </CardBody>
        </Card>

        <aside className="space-y-4 lg:sticky lg:top-24 lg:h-fit">
          <Card>
            <CardHeader title="Perkiraan biaya" />
            <CardBody className="space-y-3 text-sm">
              <div className="flex justify-between">
                <span className="text-muted">Anggaran maksimum</span>
                <span className="font-semibold text-ink">{formatRupiah(budgetMax)}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-muted">Komisi platform (10%)</span>
                <span className="font-semibold text-ink">{formatRupiah(estimatedFee)}</span>
              </div>
              <p className="border-t border-line pt-3 text-xs leading-relaxed text-subtle">
                Komisi dipotong dari bagian tenaga ahli saat dana dilepas, bukan ditambahkan ke tagihan Anda.
                Anda menyetor persis sebesar nilai kontrak yang disepakati.
              </p>
            </CardBody>
          </Card>

          <Card>
            <CardBody className="flex gap-3 text-sm text-muted">
              <Info className="mt-0.5 size-4 shrink-0 text-brand" />
              <p>
                Setelah menerima proposal, Anda dapat membagi pekerjaan menjadi beberapa milestone. Dana
                dilepas per milestone, bukan sekaligus di awal.
              </p>
            </CardBody>
          </Card>
        </aside>
      </div>
    </div>
  )
}
